import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// 3D mluvící hlava do konverzačního cvičení.
//
// Logika portovaná z avatar_service/avatar.html. Avatar v klidu mrká, dýchá a
// mírně sleduje kurzor. Když dostane props `analyser` (AnalyserNode přehrávaného
// zvuku) a `speaking=true`, odhaduje z frekvencí zvuku tvar úst a otevírá pusu —
// lip-sync tedy nezávisí na tom, jaký engine zvuk vyrobil.
//
// Veškerý měnitelný stav držíme v jednom `useRef` objektu (S), který čte animační
// smyčka. Tím se vyhneme překreslování Reactu na každý snímek.

const visemeNames = [
  'viseme_PP', 'viseme_FF', 'viseme_TH', 'viseme_DD', 'viseme_kk',
  'viseme_SS', 'viseme_CH', 'viseme_aa', 'viseme_E', 'viseme_I',
  'viseme_U', 'viseme_O', 'viseme_RR',
];

const MODEL_URL = '/avatar/avatar_morphs.glb';

// Citlivost lip-sync — vytaženo na jedno místo, ať jde ladit od oka.
// Laděno pro Kokoro (24 kHz), původní hodnoty byly pro LuxTTS (48 kHz).
const TUNING = {
  // Dělitele frekvenčních pásem (vyšší = méně citlivé).
  F1_DIV: 45,        // otevřenost čelisti (formant F1)
  F2_DIV: 35,        // poloha jazyka (F2)
  HIGH_DIV: 25,      // třené hlásky (3–8 kHz)
  VERY_DIV: 20,      // sykavky (5–12 kHz)
  LOW_DIV: 30,       // znělost (80–300 Hz)
  VISEME_GAIN: 1.9,  // zesílení výsledných visemes
  // Automatické zesílení hlasitosti — kalibrace na hlas bez pevných prahů.
  RMS_PEAK_DECAY: 0.999,   // jak pomalu klesá běžící špička hlasitosti
  RMS_PEAK_FLOOR: 0.02,    // dolní pojistka, ať ticho nezvedne zisk
  RMS_PEAK_FRACTION: 0.5,  // energy = rms / (peak * fraction)
  SILENCE_ENERGY: 0.05,    // pod touto normalizovanou energií = ticho (pusa zavřená)
  // Kokoro nemá energii nad ~12 kHz — přes tuto mez je spektrum prázdné.
  SOURCE_MAX_HZ: 12000,
};

const AvatarHead = ({ analyser = null, speaking = false, className = '', style }) => {
  const mountRef = useRef(null);
  // Props čtené uvnitř animační smyčky — drženy v refech, ať smyčka vždy vidí
  // aktuální hodnotu bez znovuvytvoření.
  const analyserRef = useRef(analyser);
  const speakingRef = useRef(speaking);
  useEffect(() => { analyserRef.current = analyser; }, [analyser]);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    // ── Stav scény a animací ────────────────────────────────────
    const S = {
      scene: null, camera: null, renderer: null,
      clock: new THREE.Clock(),
      avatarModel: null, headPivot: null,
      jawBone: null, headBone: null, morphMesh: null, morphDict: {},
      initialJawRot: 0, initialHeadRot: { x: 0, y: 0, z: 0 },
      visemeCurrent: {}, visemeTarget: {},
      currentJaw: 0, targetJaw: 0,
      rmsPeak: TUNING.RMS_PEAK_FLOOR, // běžící špička hlasitosti pro normalizaci
      // Mrkání
      nextBlink: 3, blinkPhase: 0, blinkTimer: 0,
      saccadeTimer: 0, saccadeTarget: { x: 0, y: 0 },
      // Idle výrazy
      idleLipPressTimer: 0, idleLipPressVal: 0,
      idleSwallowTimer: 40, idleSwallowPhase: 0, idleSwallowT: 0,
      idleJawTarget: 0, idleJawCurrent: 0,
      idleLookTimer: 0, idleLookTarget: { x: 0, y: 0 }, idleLookCurrent: { x: 0, y: 0 },
      idleMoodPhase: 0,
      puckerTimer: 30, puckerVal: 0, puckerTarget: 0,
      cheekTimer: 60, cheekVal: 0, cheekTarget: 0,
      browFlashTimer: 12, browFlashVal: 0, browFlashTarget: 0,
      // Sledování kurzoru
      gazeTarget: { x: 0, y: 0 }, gazeCurrent: { x: 0, y: 0 }, gazeActive: false,
      rafId: 0, disposed: false,
    };
    visemeNames.forEach((v) => { S.visemeCurrent[v] = 0; S.visemeTarget[v] = 0; });

    const GAZE_MAX_YAW = 0.55;
    const GAZE_MAX_PITCH = 0.32;
    const GAZE_SMOOTH = 7;

    const width = mount.clientWidth || 320;
    const height = mount.clientHeight || 360;

    // ── Init Three.js ───────────────────────────────────────────
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch (e) {
      // Prohlížeč bez WebGL — avatar se prostě nevykreslí, cvičení běží dál.
      console.warn('[avatar] WebGL není dostupné, avatar se nezobrazí:', e);
      return undefined;
    }
    S.renderer = renderer;
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    S.scene = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 1.52, 0.55);
    camera.lookAt(0, 1.52, 0);
    S.camera = camera;
    // Bod, na který kamera míří — nastaví se po načtení podle modelu, ať se
    // zachová i při změně velikosti okna.
    S.lookAt = new THREE.Vector3(0, 1.52, 0);

    // Odsadí kameru tak, aby byl celý model (hlava/busta) v záběru. Spočítá
    // obalový kvádr a podle úhlu záběru dopočítá potřebnou vzdálenost.
    function frameModel(rootObj) {
      const box = new THREE.Box3().setFromObject(rootObj);
      if (box.isEmpty()) return;
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      box.getCenter(center);
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y);
      const fov = (camera.fov * Math.PI) / 180;
      // 1.35 = malé okraje kolem hlavy, ať se nedotýká krajů.
      const dist = (maxDim / 2 / Math.tan(fov / 2)) * 1.35;
      camera.position.set(center.x, center.y, center.z + dist);
      camera.lookAt(center);
      S.lookAt.copy(center);
    }

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(1, 2, 2); scene.add(dir);
    const fill = new THREE.DirectionalLight(0xe0e0ff, 0.4);
    fill.position.set(-1, 1, -1); scene.add(fill);

    // ── Načtení modelu ──────────────────────────────────────────
    const loader = new GLTFLoader();
    loader.load(
      MODEL_URL,
      (gltf) => {
        if (S.disposed) return;
        S.avatarModel = gltf.scene;
        scene.add(S.avatarModel);
        discoverTargets(S.avatarModel);
        // Model bez hlavové kosti: postavíme pivot vystředěný na obličej, aby se
        // hlava při sledování kurzoru otáčela na místě (ne kolem nohou).
        if (!S.headBone) {
          const PIVOT_Y = 1.08;
          S.headPivot = new THREE.Group();
          S.headPivot.position.set(0, PIVOT_Y, 0);
          S.avatarModel.position.y = -PIVOT_Y;
          scene.remove(S.avatarModel);
          S.headPivot.add(S.avatarModel);
          scene.add(S.headPivot);
        }
        // Až teď, když je model na svém místě, podle něj zarámujeme kameru.
        frameModel(S.headPivot || S.avatarModel);
      },
      undefined,
      (err) => {
        // Model se nenačetl — avatar zůstane prázdný, cvičení funguje dál.
        console.warn('[avatar] model se nepodařilo načíst:', err);
      },
    );

    function discoverTargets(model) {
      model.traverse((child) => {
        if (child.isBone) {
          const n = child.name.toLowerCase();
          if (n.includes('jaw')) { S.jawBone = child; S.initialJawRot = child.rotation.x; }
          if (n === 'head' || (n.includes('head') && !n.includes('jaw'))) {
            S.headBone = child;
            S.initialHeadRot = { x: child.rotation.x, y: child.rotation.y, z: child.rotation.z };
          }
        }
        if (child.isMesh && child.morphTargetDictionary) {
          const count = Object.keys(child.morphTargetDictionary).length;
          if (count > Object.keys(S.morphDict).length) {
            S.morphMesh = child;
            S.morphDict = child.morphTargetDictionary;
          }
        }
      });
    }

    // ── Pomocné: morphy ─────────────────────────────────────────
    function setMorph(name, value) {
      if (!S.morphMesh || S.morphDict[name] === undefined) return;
      S.morphMesh.morphTargetInfluences[S.morphDict[name]] = value;
    }
    function getMorph(name) {
      if (!S.morphMesh || S.morphDict[name] === undefined) return 0;
      return S.morphMesh.morphTargetInfluences[S.morphDict[name]];
    }

    // ── Analýza zvuku → odhad visemes ───────────────────────────
    function analyzeAudioFrame() {
      const an = analyserRef.current;
      if (!an) return null;
      const freqData = new Uint8Array(an.frequencyBinCount);
      an.getByteFrequencyData(freqData);
      const timeData = new Uint8Array(an.fftSize);
      an.getByteTimeDomainData(timeData);

      const sr = an.context.sampleRate;
      const binHz = sr / an.fftSize;

      function bandEnergy(lowHz, highHz) {
        const lo = Math.max(1, Math.floor(lowHz / binHz));
        const hi = Math.min(freqData.length - 1, Math.ceil(highHz / binHz));
        let sum = 0, count = 0;
        for (let i = lo; i <= hi; i++) { sum += freqData[i]; count++; }
        return count > 0 ? sum / count : 0;
      }

      // Kokoro nemá energii nad ~12 kHz, horní polovina binů je prázdná. Počítáme
      // proto jen přes populované pásmo, jinak by `overall` vyšlo zhruba poloviční.
      const popBins = Math.min(freqData.length, Math.floor(TUNING.SOURCE_MAX_HZ / binHz) + 1);
      let totalEnergy = 0, weightedSum = 0;
      for (let i = 1; i < popBins; i++) {
        totalEnergy += freqData[i];
        weightedSum += freqData[i] * i * binHz;
      }
      const centroid = totalEnergy > 0 ? weightedSum / totalEnergy : 0;

      let rms = 0;
      for (let i = 0; i < timeData.length; i++) {
        const v = (timeData[i] - 128) / 128;
        rms += v * v;
      }
      rms = Math.sqrt(rms / timeData.length);

      let zcr = 0;
      for (let i = 1; i < timeData.length; i++) {
        if ((timeData[i] >= 128) !== (timeData[i - 1] >= 128)) zcr++;
      }
      zcr /= timeData.length;

      return {
        f1: bandEnergy(250, 900), f2: bandEnergy(800, 2500), f3: bandEnergy(2500, 4000),
        lowBand: bandEnergy(80, 300), highBand: bandEnergy(3000, 8000),
        veryHigh: bandEnergy(5000, 12000), centroid, rms, zcr,
        overall: totalEnergy / popBins,
      };
    }

    function estimateVisemes(analysis) {
      visemeNames.forEach((v) => { S.visemeTarget[v] = 0; });
      if (!analysis) { S.targetJaw = 0; return; }
      const { f1, f2, lowBand, highBand, veryHigh, centroid, rms, zcr, overall } = analysis;

      // Hlasitost měříme relativně k běžící špičce — heuristika se tak naladí na
      // jakýkoli hlas sama, bez pevných prahů závislých na úrovni signálu.
      S.rmsPeak = Math.max(rms, S.rmsPeak * TUNING.RMS_PEAK_DECAY, TUNING.RMS_PEAK_FLOOR);
      const energy = Math.min(1, rms / (S.rmsPeak * TUNING.RMS_PEAK_FRACTION));
      if (energy < TUNING.SILENCE_ENERGY) { S.targetJaw = 0; return; }

      const f1n = Math.min(1, f1 / TUNING.F1_DIV);
      const f2n = Math.min(1, f2 / TUNING.F2_DIV);
      const fricative = Math.min(1, highBand / TUNING.HIGH_DIV);
      const sibilant = Math.min(1, veryHigh / TUNING.VERY_DIV);
      const voiced = Math.min(1, lowBand / TUNING.LOW_DIV);

      S.targetJaw = Math.pow(f1n, 0.9) * energy;

      if (f1n > 0.5 && f2n > 0.2 && f2n < 0.7) S.visemeTarget['viseme_aa'] = f1n * energy * 0.9;
      if (f1n < 0.5 && f2n > 0.5) {
        const eeAmount = (1 - f1n) * f2n * energy;
        S.visemeTarget['viseme_E'] = eeAmount * 0.7;
        S.visemeTarget['viseme_I'] = eeAmount * 0.3;
      }
      if (f1n < 0.4 && f2n < 0.35 && voiced > 0.3) S.visemeTarget['viseme_U'] = (1 - f1n) * (1 - f2n) * energy * 0.8;
      if (f1n > 0.3 && f1n < 0.7 && f2n < 0.4 && voiced > 0.2) S.visemeTarget['viseme_O'] = f1n * (1 - f2n) * energy * 0.7;
      if (sibilant > 0.3 && zcr > 0.15) { S.visemeTarget['viseme_SS'] = Math.min(0.8, sibilant * 1.2); S.targetJaw *= 0.3; }
      if (fricative > 0.3 && sibilant < 0.3 && zcr > 0.1) { S.visemeTarget['viseme_CH'] = Math.min(0.7, fricative); S.targetJaw *= 0.4; }
      if (fricative > 0.2 && energy < 0.4 && f1n < 0.3) { S.visemeTarget['viseme_FF'] = Math.min(0.6, fricative * 0.8); S.targetJaw *= 0.3; }
      if (energy > 0.1 && energy < 0.4 && fricative < 0.15) {
        if (voiced > 0.2) { S.visemeTarget['viseme_PP'] = Math.min(0.6, (1 - energy) * voiced * 0.8); S.targetJaw *= 0.2; }
        else { S.visemeTarget['viseme_DD'] = Math.min(0.5, energy * 0.8); S.targetJaw *= 0.4; }
      }
      if (energy > 0.5 && overall > 30 && fricative < 0.2) {
        if (centroid > 1500) S.visemeTarget['viseme_kk'] = Math.min(0.5, energy * 0.5);
        else S.visemeTarget['viseme_PP'] = Math.min(0.5, energy * 0.4);
      }
      if (energy > 0.2 && f2n > 0.3 && f2n < 0.6 && fricative < 0.15 && f1n > 0.2 && f1n < 0.5) {
        S.visemeTarget['viseme_RR'] = Math.min(0.5, energy * 0.4);
      }
      if (fricative > 0.1 && fricative < 0.3 && energy < 0.3 && f1n < 0.3) {
        S.visemeTarget['viseme_TH'] = Math.min(0.4, fricative * 0.6);
      }
      let totalW = 0;
      visemeNames.forEach((v) => { totalW += S.visemeTarget[v]; });
      if (totalW > 1.5) {
        const scale = 1.5 / totalW;
        visemeNames.forEach((v) => { S.visemeTarget[v] *= scale; });
      }
    }

    // ── Idle: mrkání ────────────────────────────────────────────
    function updateBlink(dt) {
      const t = S.clock.getElapsedTime();
      if (S.blinkPhase === 0 && t >= S.nextBlink) { S.blinkPhase = 1; S.blinkTimer = 0; }
      if (S.blinkPhase > 0) {
        S.blinkTimer += dt;
        let v = 0;
        if (S.blinkPhase === 1) {
          v = Math.min(1, S.blinkTimer / 0.08);
          if (S.blinkTimer >= 0.08) { S.blinkPhase = 2; S.blinkTimer = 0; }
        } else {
          v = Math.max(0, 1 - S.blinkTimer / 0.18);
          if (S.blinkTimer >= 0.18) {
            S.blinkPhase = 0; v = 0;
            const rare = Math.random() < 0.10;
            S.nextBlink = t + (rare ? 0.25 : 2.5 + Math.random() * 4.0);
          }
        }
        setMorph('eyeBlinkLeft', v);
        setMorph('eyeBlinkRight', v);
      }
    }

    // ── Idle: pohyb hlavy + sledování kurzoru ───────────────────
    function updateIdleHead(dt) {
      const headXform = S.headBone || S.headPivot;
      const baseRot = S.headBone ? S.initialHeadRot : { x: 0, y: 0, z: 0 };
      if (!headXform) return;
      const t = S.clock.getElapsedTime();

      const breathPhase = t * 1.47;
      const breath = Math.sin(breathPhase) * 0.005 + Math.sin(breathPhase * 2) * 0.001;
      const driftX = Math.sin(t * 0.19) * 0.02 + Math.sin(t * 0.53) * 0.01;
      const driftY = Math.sin(t * 0.29) * 0.018 + Math.sin(t * 0.73) * 0.008;
      const driftZ = Math.sin(t * 0.23) * 0.01 + Math.sin(t * 0.41) * 0.005;

      S.idleLookTimer -= dt;
      if (S.idleLookTimer <= 0) {
        S.idleLookTarget.x = (Math.random() - 0.5) * 0.025;
        S.idleLookTarget.y = (Math.random() - 0.5) * 0.03;
        S.idleLookTimer = 1.5 + Math.random() * 4;
      }
      S.idleLookCurrent.x += (S.idleLookTarget.x - S.idleLookCurrent.x) * dt * 3;
      S.idleLookCurrent.y += (S.idleLookTarget.y - S.idleLookCurrent.y) * dt * 3;

      S.saccadeTimer -= dt;
      if (S.saccadeTimer <= 0) {
        S.saccadeTarget.x = (Math.random() - 0.5) * 0.006;
        S.saccadeTarget.y = (Math.random() - 0.5) * 0.005;
        S.saccadeTimer = 0.08 + Math.random() * 0.25;
      }

      // Při řeči koukáme rovně, jinak na kurzor.
      const forceCenter = speakingRef.current;
      const gazeGoalX = forceCenter ? 0 : S.gazeTarget.x;
      const gazeGoalY = forceCenter ? 0 : S.gazeTarget.y;
      S.gazeCurrent.x += (gazeGoalX - S.gazeCurrent.x) * dt * GAZE_SMOOTH;
      S.gazeCurrent.y += (gazeGoalY - S.gazeCurrent.y) * dt * GAZE_SMOOTH;

      const gazeYaw = S.gazeCurrent.x * GAZE_MAX_YAW;
      const gazePitch = -S.gazeCurrent.y * GAZE_MAX_PITCH;

      if (!speakingRef.current) {
        headXform.rotation.x = baseRot.x + driftX + S.idleLookCurrent.x + S.saccadeTarget.x + breath + gazePitch;
        headXform.rotation.y = baseRot.y + driftY + S.idleLookCurrent.y + S.saccadeTarget.y + gazeYaw;
        headXform.rotation.z = baseRot.z + driftZ;
      }
    }

    // ── Idle: výrazy obličeje ───────────────────────────────────
    function updateIdleExpressions(dt) {
      if (speakingRef.current) return;
      const t = S.clock.getElapsedTime();

      S.idleMoodPhase += dt * 0.05;
      const mood = Math.sin(S.idleMoodPhase) * 0.5 + 0.5;

      const browBase = 0.10 + Math.max(0, Math.sin(t * 0.18)) * 0.15 + mood * 0.10;
      setMorph('browInnerUp', browBase);

      const smileBase = 0.15 + mood * 0.20 + Math.sin(t * 0.09) * 0.08;
      const smileAsym = Math.sin(t * 0.13) * 0.06;
      setMorph('mouthSmileLeft', Math.max(0, smileBase + smileAsym));
      setMorph('mouthSmileRight', Math.max(0, smileBase - smileAsym));

      const squintBase = Math.max(0, smileBase - 0.05) * 0.6;
      setMorph('eyeSquintLeft', squintBase + Math.sin(t * 0.15) * 0.06);
      setMorph('eyeSquintRight', squintBase + Math.sin(t * 0.15) * 0.06);

      setMorph('noseSneerLeft', 0);
      setMorph('noseSneerRight', 0);

      S.idleLipPressTimer -= dt;
      if (S.idleLipPressTimer <= 0) {
        S.idleLipPressVal = 0.4 + Math.random() * 0.35;
        S.idleLipPressTimer = 8 + Math.random() * 12;
      }
      if (S.idleLipPressVal > 0.01) {
        setMorph('viseme_PP', S.idleLipPressVal);
        S.idleLipPressVal *= (1 - dt * 1.8);
      } else {
        setMorph('viseme_PP', 0);
      }

      S.idleSwallowTimer -= dt;
      if (S.idleSwallowTimer <= 0 && S.idleSwallowPhase === 0) {
        S.idleSwallowPhase = 1; S.idleSwallowT = 0;
        S.idleSwallowTimer = 40 + Math.random() * 50;
      }
      if (S.idleSwallowPhase > 0) {
        S.idleSwallowT += dt;
        if (S.idleSwallowPhase === 1) {
          S.idleJawTarget = Math.min(0.12, S.idleSwallowT * 1.5);
          if (S.idleSwallowT > 0.15) { S.idleSwallowPhase = 2; S.idleSwallowT = 0; }
        } else {
          S.idleJawTarget = Math.max(0, 0.12 - S.idleSwallowT * 0.8);
          if (S.idleSwallowT > 0.2) { S.idleSwallowPhase = 0; S.idleJawTarget = 0; }
        }
      }
      S.idleJawCurrent += (S.idleJawTarget - S.idleJawCurrent) * dt * 12;
      if (!speakingRef.current) {
        if (S.jawBone && S.jawBone.isBone) {
          S.jawBone.rotation.x = S.initialJawRot + S.idleJawCurrent * 0.2;
        } else if (S.morphMesh && S.morphDict['jawOpen'] !== undefined) {
          setMorph('jawOpen', S.idleJawCurrent * 1.4);
        }
      }

      const breathCycle = Math.sin(t * 1.47);
      const breathIn = Math.max(0, breathCycle);
      setMorph('breathe', breathIn * 0.8);

      const thinkMouth = Math.sin(t * 0.4) * Math.sin(t * 0.13);
      if (thinkMouth > 0.3) setMorph('viseme_RR', (thinkMouth - 0.3) * 0.15);
      else setMorph('viseme_RR', 0);

      S.puckerTimer -= dt;
      if (S.puckerTimer <= 0 && S.puckerTarget < 0.01) {
        S.puckerTarget = 0.45 + Math.random() * 0.25;
        setTimeout(() => { S.puckerTarget = 0; }, 300 + Math.random() * 350);
        S.puckerTimer = 14 + Math.random() * 20;
      }
      S.puckerVal += (S.puckerTarget - S.puckerVal) * Math.min(1, dt * 7);
      setMorph('mouthPucker', S.puckerVal);

      S.cheekTimer -= dt;
      if (S.cheekTimer <= 0 && S.cheekTarget < 0.01) {
        S.cheekTarget = 0.15 + Math.random() * 0.15;
        setTimeout(() => { S.cheekTarget = 0; }, 300 + Math.random() * 400);
        S.cheekTimer = 60 + Math.random() * 90;
      }
      S.cheekVal += (S.cheekTarget - S.cheekVal) * Math.min(1, dt * 6);
      setMorph('cheekPuff', S.cheekVal);

      S.browFlashTimer -= dt;
      if (S.browFlashTimer <= 0 && S.browFlashTarget < 0.01) {
        S.browFlashTarget = 0.6 + Math.random() * 0.3;
        setTimeout(() => { S.browFlashTarget = 0; }, 250 + Math.random() * 250);
        S.browFlashTimer = 6 + Math.random() * 8;
      }
      S.browFlashVal += (S.browFlashTarget - S.browFlashVal) * Math.min(1, dt * 10);
      const currentInner = getMorph('browInnerUp');
      setMorph('browInnerUp', Math.min(1, currentInner + S.browFlashVal));
      const outerDriftL = Math.max(0, Math.sin(t * 0.14) * 0.08);
      const outerDriftR = Math.max(0, Math.sin(t * 0.17 + 1.3) * 0.08);
      setMorph('browOuterUpLeft', outerDriftL + S.browFlashVal * 0.7);
      setMorph('browOuterUpRight', outerDriftR + S.browFlashVal * 0.7);
    }

    // ── Řeč: visemes + čelist ───────────────────────────────────
    function updateSpeech(dt) {
      const isSpeaking = speakingRef.current;
      if (isSpeaking && analyserRef.current) {
        estimateVisemes(analyzeAudioFrame());
      } else if (!isSpeaking) {
        visemeNames.forEach((v) => { S.visemeTarget[v] = 0; });
        S.targetJaw = 0;
      }

      const visemeSpeed = 15;
      visemeNames.forEach((v) => {
        const diff = S.visemeTarget[v] - S.visemeCurrent[v];
        const speed = diff > 0 ? visemeSpeed : visemeSpeed * 0.7;
        S.visemeCurrent[v] += diff * Math.min(1, dt * speed);
        if (Math.abs(S.visemeCurrent[v]) < 0.001) S.visemeCurrent[v] = 0;
        if (isSpeaking || S.visemeCurrent[v] > 0.001) {
          setMorph(v, S.visemeCurrent[v] * TUNING.VISEME_GAIN);
        }
      });

      const jawDiff = S.targetJaw - S.currentJaw;
      const jawSpeed = jawDiff > 0 ? 20 : 12;
      S.currentJaw += jawDiff * Math.min(1, dt * jawSpeed);
      if (Math.abs(S.currentJaw) < 0.001) S.currentJaw = 0;

      if (S.jawBone) {
        if (S.jawBone.isBone) S.jawBone.rotation.x = S.initialJawRot + S.currentJaw * 0.38;
        else S.jawBone.position.y = -0.2 - S.currentJaw * 0.05;
      } else if (S.morphMesh && S.morphDict['jawOpen'] !== undefined) {
        setMorph('jawOpen', S.currentJaw * 0.5);
      }

      // Pohyb hlavy a výraz při řeči
      const headXform = S.headBone || S.headPivot;
      const baseRot = S.headBone ? S.initialHeadRot : { x: 0, y: 0, z: 0 };
      if (isSpeaking && headXform) {
        const t = S.clock.getElapsedTime();
        const energy = S.currentJaw;
        const nod = Math.sin(t * 3.2) * 0.008 * energy;
        const tilt = Math.sin(t * 2.1) * 0.005 * energy;
        const turn = Math.sin(t * 1.5) * 0.006 * energy;
        const breath = Math.sin(t * 1.57) * 0.002;
        headXform.rotation.x = baseRot.x + nod + breath + Math.sin(t * 0.3) * 0.004 + S.gazeCurrent.y * GAZE_MAX_PITCH * 0.3;
        headXform.rotation.y = baseRot.y + turn + Math.sin(t * 0.5) * 0.003 + S.gazeCurrent.x * GAZE_MAX_YAW * 0.3;
        headXform.rotation.z = baseRot.z + tilt;
      }
      if (isSpeaking) {
        const t = S.clock.getElapsedTime();
        const energy = S.currentJaw;
        // Vynulovat klidové morphy, které řeč jinak neřídí — jinak by zůstaly
        // zaseknuté z posledního klidového snímku (našpulená/nafouknutá pusa).
        setMorph('mouthPucker', 0);
        setMorph('cheekPuff', 0);
        if (energy > 0.4) {
          const emphasis = (energy - 0.4) * 0.5;
          setMorph('browInnerUp', emphasis);
          setMorph('browOuterUpLeft', emphasis * 0.3);
          setMorph('browOuterUpRight', emphasis * 0.3);
        }
        const smileBase = 0.05 + energy * 0.1;
        setMorph('mouthSmileLeft', smileBase + Math.sin(t * 0.7) * 0.02);
        setMorph('mouthSmileRight', smileBase + Math.sin(t * 0.7) * 0.02);
        if (energy > 0.5) {
          setMorph('eyeSquintLeft', (energy - 0.5) * 0.2);
          setMorph('eyeSquintRight', (energy - 0.5) * 0.2);
        }
      }
    }

    // ── Hlavní smyčka ───────────────────────────────────────────
    function animate() {
      if (S.disposed) return;
      S.rafId = requestAnimationFrame(animate);
      const dt = Math.min(S.clock.getDelta(), 0.05);
      updateBlink(dt);
      updateIdleHead(dt);
      updateIdleExpressions(dt);
      updateSpeech(dt);
      renderer.render(scene, camera);
    }
    animate();

    // ── Sledování kurzoru ───────────────────────────────────────
    const onMouseMove = (e) => {
      S.gazeTarget.x = (e.clientX / window.innerWidth) * 2 - 1;
      S.gazeTarget.y = -((e.clientY / window.innerHeight) * 2 - 1);
      S.gazeActive = true;
    };
    window.addEventListener('mousemove', onMouseMove);

    // ── Reakce na změnu velikosti kontejneru ────────────────────
    const ro = new ResizeObserver(() => {
      const w = mount.clientWidth || width;
      const h = mount.clientHeight || height;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(mount);

    // ── Úklid ───────────────────────────────────────────────────
    return () => {
      S.disposed = true;
      cancelAnimationFrame(S.rafId);
      window.removeEventListener('mousemove', onMouseMove);
      ro.disconnect();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => {
            Object.values(m).forEach((val) => { if (val && val.isTexture) val.dispose(); });
            m.dispose();
          });
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className={className} style={style} />;
};

export default AvatarHead;
